import React, { useState } from 'react';
import { Users, Plus, X, ArrowLeft, ArrowRight, Mail, Crown, Shield, User, Baby, AlertCircle, CheckCircle, Info, SkipForward } from 'lucide-react';
import { mapNpubToUserDuid } from '../lib/family-foundry-integration';
import { InvitationGenerator } from './family-invitations';

interface TrustedPeer {
  id: string;
  name: string;
  npub: string;
  role: string;
  relationship: string;
  invited: boolean;
}

interface FamilyFoundryStep3InviteProps {
  trustedPeers: TrustedPeer[];
  onPeersChange: (peers: TrustedPeer[]) => void;
  onNext: () => void;
  onBack: () => void;
  /** If true, allows proceeding without adding any peers (solo founder mode) */
  allowSkip?: boolean;
  /** Federation DUID for generating invitations (available after federation creation) */
  federationDuid?: string;
  /** Federation name for invitation display */
  federationName?: string;
}

const FamilyFoundryStep3Invite: React.FC<FamilyFoundryStep3InviteProps> = ({
  trustedPeers,
  onPeersChange,
  onNext,
  onBack,
  allowSkip = true, // Default to allowing skip for solo founder support
  federationDuid,
  federationName
}) => {
  const [newPeer, setNewPeer] = useState({
    name: '',
    npub: '',
    role: '',
    relationship: ''
  });
  const [validationErrors, setValidationErrors] = useState<Map<string, string>>(new Map());
  const [isValidating, setIsValidating] = useState(false);
  const [validatedPeers, setValidatedPeers] = useState<Set<string>>(new Set());

  const roleIcons = {
    guardian: Crown,
    steward: Shield,
    adult: User,
    offspring: Baby
  };

  const roleColors = {
    guardian: 'from-red-600 to-orange-600',
    steward: 'from-purple-600 to-blue-600',
    adult: 'from-green-600 to-teal-600',
    offspring: 'from-yellow-600 to-orange-600'
  };

  /**
   * Validate that an npub exists in the system
   */
  const validateNpubExists = async (npub: string): Promise<boolean> => {
    try {
      // Import supabase dynamically to avoid circular dependencies
      const { supabase } = await import('../lib/supabase');
      await mapNpubToUserDuid(npub, supabase);
      return true;
    } catch (error) {
      return false;
    }
  };

  const addPeer = async () => {
    if (!newPeer.name.trim() || !newPeer.npub.trim() || !newPeer.role || !newPeer.relationship) {
      return;
    }

    // Validate npub exists
    setIsValidating(true);
    const npubExists = await validateNpubExists(newPeer.npub.trim());
    setIsValidating(false);

    if (!npubExists) {
      const errors = new Map(validationErrors);
      errors.set(newPeer.npub, 'User not found for this npub');
      setValidationErrors(errors);
      return;
    }

    const peer: TrustedPeer = {
      id: Date.now().toString(),
      name: newPeer.name.trim(),
      npub: newPeer.npub.trim(),
      role: newPeer.role,
      relationship: newPeer.relationship,
      invited: false
    };
    onPeersChange([...trustedPeers, peer]);
    setValidatedPeers(new Set([...validatedPeers, newPeer.npub.trim()]));
    setNewPeer({ name: '', npub: '', role: '', relationship: '' });

    // Clear any validation errors for this npub
    const errors = new Map(validationErrors);
    errors.delete(newPeer.npub);
    setValidationErrors(errors);
  };

  const removePeer = (id: string) => {
    onPeersChange(trustedPeers.filter(peer => peer.id !== id));
  };

  const handleNext = () => {
    // If we have peers, validate that all have npub values
    if (trustedPeers.length > 0) {
      const allHaveNpubs = trustedPeers.every(peer => peer.npub.trim());
      if (allHaveNpubs) {
        onNext();
      }
    }
  };

  const handleSkip = () => {
    // Allow solo founder to proceed without any peers
    if (allowSkip) {
      onNext();
    }
  };

  const canProceedWithPeers = trustedPeers.length > 0 && trustedPeers.every(peer => peer.npub.trim());

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="text-center">
        <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-purple-600 to-blue-600 rounded-full mb-4">
          <Users className="h-8 w-8 text-white" />
        </div>
        <h2 className="text-3xl font-bold text-white mb-2">Invite Family Members</h2>
        <p className="text-purple-200 max-w-2xl mx-auto">
          Add family members and trusted peers to your federation using their Nostr public keys.
        </p>
        {allowSkip && (
          <div className="mt-4 inline-flex items-center gap-2 bg-blue-500/20 border border-blue-400/30 rounded-lg px-4 py-2">
            <Info className="h-4 w-4 text-blue-300" />
            <span className="text-blue-200 text-sm">
              This step is optional. You can create your federation now and invite members later.
            </span>
          </div>
        )}
      </div>

      {/* Out-of-Band Invitation Generator */}
      {federationDuid && (
        <div className="bg-gradient-to-br from-green-500/10 to-blue-500/10 border border-green-400/30 rounded-xl p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-green-500/20 rounded-full flex items-center justify-center">
              <Mail className="h-5 w-5 text-green-400" />
            </div>
            <div>
              <h3 className="text-xl font-bold text-white">Generate Invitation Links</h3>
              <p className="text-green-300 text-sm">Works for people who don't have Nostr yet!</p>
            </div>
          </div>

          <div className="bg-white/5 rounded-lg p-4 mb-4 border border-white/10">
            <h4 className="text-white font-semibold mb-2">📨 Out-of-Band Invitations</h4>
            <ul className="text-purple-200 text-sm space-y-1">
              <li>• Generate role-specific invitation links (Guardian, Steward, Adult, Offspring)</li>
              <li>• Share via Signal, Email, SMS, or any messaging app</li>
              <li>• QR codes for face-to-face onboarding</li>
              <li>• Invitees will create their Nostr identity during signup</li>
            </ul>
          </div>

          <InvitationGenerator
            federationDuid={federationDuid}
            federationName={federationName || 'Family Federation'}
            onInvitationGenerated={(invitation) => {
              console.log('Invitation generated:', invitation);
            }}
          />
        </div>
      )}

      {!federationDuid && (
        <div className="bg-amber-500/10 border border-amber-400/30 rounded-xl p-6">
          <div className="flex items-center gap-3 mb-2">
            <Info className="h-5 w-5 text-amber-400" />
            <h3 className="text-lg font-bold text-white">Invitation Links Available After Federation Creation</h3>
          </div>
          <p className="text-amber-200 text-sm">
            Once your federation is created, you'll be able to generate invitation links and QR codes
            to share with family members who don't have Nostr identities yet.
          </p>
        </div>
      )}

      {/* Add New Peer */}
      <div className="bg-white/5 border border-white/10 rounded-xl p-6">
        <h3 className="text-xl font-bold text-white mb-4">Add Custom Family Member</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          <div>
            <label className="block text-white font-semibold mb-2">Name</label>
            <input
              type="text"
              value={newPeer.name}
              onChange={(e) => setNewPeer({ ...newPeer, name: e.target.value })}
              placeholder="Enter family member name"
              className="w-full bg-white/10 border border-white/20 rounded-lg px-4 py-3 text-white placeholder-purple-300 focus:outline-none focus:ring-2 focus:ring-purple-500"
            />
          </div>
          <div>
            <label className="block text-white font-semibold mb-2">Nostr Public Key (npub)</label>
            <input
              type="text"
              value={newPeer.npub}
              onChange={(e) => setNewPeer({ ...newPeer, npub: e.target.value })}
              placeholder="npub1..."
              className="w-full bg-white/10 border border-white/20 rounded-lg px-4 py-3 text-white placeholder-purple-300 focus:outline-none focus:ring-2 focus:ring-purple-500"
            />
          </div>
          <div>
            <label className="block text-white font-semibold mb-2">Role</label>
            <select
              value={newPeer.role}
              onChange={(e) => setNewPeer({ ...newPeer, role: e.target.value })}
              className="w-full bg-white/10 border border-white/20 rounded-lg px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
            >
              <option value="">Select a role</option>
              <option value="guardian">Guardian</option>
              <option value="steward">Steward</option>
              <option value="adult">Adult</option>
              <option value="offspring">Offspring</option>
            </select>
          </div>
          <div>
            <label className="block text-white font-semibold mb-2">Relationship</label>
            <select
              value={newPeer.relationship}
              onChange={(e) => setNewPeer({ ...newPeer, relationship: e.target.value })}
              className="w-full bg-white/10 border border-white/20 rounded-lg px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
            >
              <option value="">Select relationship</option>
              <option value="offspring">Offspring</option>
              <option value="kin">Kin</option>
              <option value="peer">Peer</option>
            </select>
          </div>
        </div>
        {validationErrors.has(newPeer.npub) && (
          <div className="flex items-center gap-2 bg-red-500/10 border border-red-500/30 rounded-lg p-3 mb-4">
            <AlertCircle className="h-4 w-4 text-red-400" />
            <span className="text-red-300 text-sm">{validationErrors.get(newPeer.npub)}</span>
          </div>
        )}
        <button
          onClick={addPeer}
          disabled={!newPeer.name.trim() || !newPeer.npub.trim() || !newPeer.role || !newPeer.relationship || isValidating}
          className="flex items-center gap-2 bg-purple-600 hover:bg-purple-700 text-white font-bold py-3 px-6 rounded-lg transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isValidating ? (
            <>
              <div className="animate-spin">
                <Plus className="h-4 w-4" />
              </div>
              Validating...
            </>
          ) : (
            <>
              <Plus className="h-4 w-4" />
              Add Family Member
            </>
          )}
        </button>
      </div>

      {/* Current Peers */}
      {trustedPeers.length > 0 && (
        <div className="bg-white/5 border border-white/10 rounded-xl p-6">
          <h3 className="text-xl font-bold text-white mb-4">Family Members ({trustedPeers.length})</h3>
          <div className="space-y-3">
            {trustedPeers.map((peer) => {
              const IconComponent = roleIcons[peer.role as keyof typeof roleIcons];
              const colorClass = roleColors[peer.role as keyof typeof roleColors];

              return (
                <div key={peer.id} className="flex items-center justify-between bg-white/10 rounded-lg p-4">
                  <div className="flex items-center gap-4 flex-1">
                    <div className={`inline-flex items-center justify-center w-10 h-10 bg-gradient-to-br ${colorClass} rounded-full`}>
                      <IconComponent className="h-5 w-5 text-white" />
                    </div>
                    <div className="flex-1">
                      <h4 className="text-white font-semibold">{peer.name}</h4>
                      <p className="text-purple-200 text-sm">{peer.npub}</p>
                      <div className="flex gap-2 mt-1">
                        <span className="bg-purple-600 text-white px-2 py-1 rounded text-xs capitalize">
                          {peer.role}
                        </span>
                        <span className="bg-blue-600 text-white px-2 py-1 rounded text-xs capitalize">
                          {peer.relationship}
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    {validatedPeers.has(peer.npub) && (
                      <div className="flex items-center gap-1 text-green-400">
                        <CheckCircle className="h-4 w-4" />
                        <span className="text-xs">Verified</span>
                      </div>
                    )}
                    <button
                      onClick={() => removePeer(peer.id)}
                      className="text-red-400 hover:text-red-300 transition-colors"
                    >
                      <X className="h-5 w-5" />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Navigation */}
      <div className="flex justify-between pt-6">
        <button
          onClick={onBack}
          className="flex items-center gap-2 bg-white/10 hover:bg-white/20 text-white font-bold py-3 px-6 rounded-lg transition-all duration-300"
        >
          <ArrowLeft className="h-4 w-4" />
          Back
        </button>

        <div className="flex items-center gap-3">
          {/* Skip button - only show if allowSkip is true and no peers added */}
          {allowSkip && trustedPeers.length === 0 && (
            <button
              onClick={handleSkip}
              className="flex items-center gap-2 bg-amber-600/80 hover:bg-amber-600 text-white font-bold py-3 px-6 rounded-lg transition-all duration-300"
              title="Create federation now and invite members later"
            >
              <SkipForward className="h-4 w-4" />
              Skip & Create Federation
            </button>
          )}

          {/* Next with peers button - enabled when peers are added and validated */}
          <button
            onClick={handleNext}
            disabled={!canProceedWithPeers}
            className="flex items-center gap-2 bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white font-bold py-3 px-6 rounded-lg transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {trustedPeers.length > 0 ? `Next with ${trustedPeers.length} Member${trustedPeers.length > 1 ? 's' : ''}` : 'Next Step'}
            <ArrowRight className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default FamilyFoundryStep3Invite;